export type EmbedPlatform =
  | 'soundcloud'
  | 'spotify'
  | 'youtube'
  | 'apple_music'
  | 'deezer'
  | 'tidal'
  | 'traxsource';

export interface EmbedData {
  platform: EmbedPlatform;
  embedUrl: string;
  thumbnailUrl?: string;
}

const EMBED_PATTERNS: {
  platform: EmbedPlatform;
  match: RegExp;
  toEmbed: (m: RegExpMatchArray) => string;
  thumbnail?: (m: RegExpMatchArray) => string;
}[] = [
  {
    platform: 'spotify',
    match:
      /(?:open\.spotify\.com|spotify\.com)\/(track|album|playlist|episode|artist)\/([a-zA-Z0-9]+)/,
    toEmbed: (m) => `https://open.spotify.com/embed/${m[1]}/${m[2]}`,
  },
  {
    platform: 'youtube',
    match:
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    toEmbed: (m) => `https://www.youtube.com/embed/${m[1]}`,
    thumbnail: (m) =>
      `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg`,
  },
  {
    platform: 'soundcloud',
    match: /soundcloud\.com\/([a-zA-Z0-9_-]+\/[a-zA-Z0-9_-]+)/,
    toEmbed: (m) => {
      const originalUrl = `https://soundcloud.com/${m[1]}`;
      const encoded = encodeURIComponent(originalUrl);
      return `https://w.soundcloud.com/player/?url=${encoded}&color=%23ff5500&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false`;
    },
  },
  {
    platform: 'apple_music',
    match:
      /music\.apple\.com\/([a-z]{2})\/(album|playlist|song)\/([^/?]+)\/([a-zA-Z0-9.]+)/,
    toEmbed: (m) =>
      `https://embed.music.apple.com/${m[1]}/${m[2]}/${m[3]}/${m[4]}`,
  },
  {
    platform: 'deezer',
    match:
      /deezer\.com\/(?:[a-z]{2}\/)?(track|album|playlist)\/(\d+)/,
    toEmbed: (m) =>
      `https://widget.deezer.com/widget/dark/${m[1]}/${m[2]}`,
  },
  {
    platform: 'tidal',
    match:
      /tidal\.com\/(?:browse\/)?(track|album|playlist)\/([a-zA-Z0-9-]+)/,
    toEmbed: (m) =>
      `https://embed.tidal.com/${m[1]}s/${m[2]}`,
  },
  {
    platform: 'traxsource',
    match:
      /traxsource\.com\/(track|title)\/(\d+)/,
    toEmbed: (m) =>
      `https://w.traxsource.com/embedplayer/${m[1]}/${m[2]}`,
  },
];

/**
 * Parse a raw URL and return embed data for supported music/video platforms.
 * Returns null if the URL does not match any known platform pattern.
 */
export function parseEmbedUrl(url: string): EmbedData | null {
  const trimmed = url.trim();
  if (!trimmed) return null;

  for (const pattern of EMBED_PATTERNS) {
    const match = trimmed.match(pattern.match);
    if (match) {
      return {
        platform: pattern.platform,
        embedUrl: pattern.toEmbed(match),
        thumbnailUrl: pattern.thumbnail?.(match),
      };
    }
  }

  return null;
}

/**
 * Return the recommended iframe height for a given embed platform.
 * When `originalUrl` is provided, the height adapts to the content type
 * (e.g. a single track vs an album/playlist).
 */
export function getEmbedHeight(
  platform: EmbedPlatform,
  originalUrl?: string,
): number {
  const url = originalUrl ?? '';

  switch (platform) {
    case 'soundcloud':
      return 166;
    case 'youtube':
      return 315;
    case 'spotify':
      // album/playlist/artist → taller to show tracklist
      if (/\/(album|playlist|artist)\//.test(url)) return 380;
      return 152;
    case 'apple_music':
      // album/playlist → taller to show tracklist
      if (/\/(album|playlist)\//.test(url)) return 450;
      return 175;
    case 'deezer':
      if (/\/(album|playlist)\//.test(url)) return 380;
      return 152;
    case 'tidal':
      if (/\/(album|playlist)\//.test(url)) return 380;
      return 176;
    case 'traxsource':
      return 130;
  }
}

/**
 * Determine whether a post is an embed (platform slug) rather than a native
 * audio upload. Real MIME types contain '/' (e.g. 'audio/mpeg'), while
 * platform slugs (e.g. 'soundcloud', 'spotify') never do.
 *
 * Returns false for null/undefined — no MIME type means not an embed post.
 */
export function isEmbedPost(
  audioMimeType: string | null | undefined,
): boolean {
  if (!audioMimeType) return false;
  return !audioMimeType.includes('/');
}
